import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, apiBaseUrl, useApi } from '@/api/client';
import { isDecisionsModel } from '@/components/DecisionsBadge';
import { fmtNumber, fmtUsd } from '@/lib/format';
import type {
  DecisionAnswer, DecisionPiiMode, DecisionQuestion, DecisionQuestionType, DecisionRequest, DecisionResponse
} from '@/types';

interface ChoiceOption { label: string; description: string }

/** One question as edited here; turned into the wire shape by toWire(). */
interface QuestionDraft {
  key: number;
  name: string;
  type: DecisionQuestionType;
  instructions: string;
  options: ChoiceOption[];
  yesText: string;
  noText: string;
  levels: string[];
}

const typeLabels: Record<DecisionQuestionType, string> = { Choice: 'Choice', Noul: 'Yes / no', Score: 'Score' };

const piiOptions: { value: DecisionPiiMode; label: string; hint: string }[] = [
  { value: 'Reject', label: 'Reject if found', hint: 'Checked locally for emails, phone and card numbers, IBANs and NI numbers; the request is refused if any turn up. Nothing leaves Key Travel for the check.' },
  { value: 'Redact', label: 'Redact', hint: 'Personal data in the input is replaced with tokens before it is sent. Answers are labels, so nothing needs restoring.' },
  { value: 'None', label: 'None — send as written', hint: 'The input goes to the supplier exactly as typed.' }
];

const friendlyByCode: Record<string, string> = {
  pii_detected: 'Personal data was found in the input or questions, so nothing was sent. Remove it, or switch Personal data to Redact.',
  pii_redaction_unavailable: 'The redaction service is unavailable. Try again shortly, or use Reject if the input holds no personal data.',
  pii_attachments_unsupported: 'Images can’t be scanned for personal data. Tick "Send images unscanned", or set Personal data to None.',
  model_is_not_decisions: 'That model writes text rather than making decisions — use the Chat tab for it.',
  model_not_allowed: 'That model isn’t enabled for you. Pick another, or ask an administrator.',
  budget_exhausted: 'Your monthly budget is used up. Ask an administrator to raise it, or wait until next month.',
  supplier_error: 'The supplier returned an error. This is usually transient — try again in a moment.',
  supplier_key_missing: 'No licence key is configured for this supplier yet. An administrator needs to add one under Suppliers.',
  caller_not_registered: 'You are not registered for the AI gateway yet. Request access from the Home page first.',
  caller_not_active: 'Your registration is not active yet. An administrator still needs to approve it.'
};

let nextKey = 1;
function blankQuestion(type: DecisionQuestionType = 'Choice'): QuestionDraft {
  return {
    key: nextKey++, name: '', type, instructions: '',
    options: [{ label: '', description: '' }, { label: '', description: '' }],
    yesText: '', noText: '', levels: ['', '']
  };
}

function exampleQuestions(): QuestionDraft[] {
  return [
    {
      ...blankQuestion('Choice'), name: 'category', instructions: 'What is the customer asking for?',
      options: [
        { label: 'booking', description: 'A new booking' },
        { label: 'cancellation', description: 'Cancel an existing booking' },
        { label: 'complaint', description: 'Unhappy about something that happened' },
        { label: 'other', description: 'Anything else' }
      ]
    },
    { ...blankQuestion('Noul'), name: 'needs_agent', instructions: 'Does this need a person to action it?' },
    {
      ...blankQuestion('Score'), name: 'urgency', instructions: 'How urgent is it?',
      levels: ['Routine, within a few days', 'Today', 'Immediately, travel imminent']
    }
  ];
}

const exampleInput = 'Hi, please cancel my hotel booking for 12 November. My plans have changed. Thanks.';

const namePattern = /^[A-Za-z0-9_-]{1,64}$/;

/** What's wrong with a question, in words, or null when it's ready to send. */
function questionProblem(q: QuestionDraft, index: number, all: QuestionDraft[]): string | null {
  const n = index + 1;
  if (!namePattern.test(q.name)) return `Question ${n}: give it a name of letters, digits, _ or - (no spaces).`;
  if (all.findIndex(o => o.name === q.name) !== index) return `Question ${n}: "${q.name}" is already used — names must be unique.`;
  if (q.type === 'Choice') {
    const labels = q.options.map(o => o.label.trim()).filter(Boolean);
    if (labels.length < 2 || labels.length > 50) return `Question ${n}: a choice needs 2 to 50 options.`;
    if (new Set(labels).size !== labels.length) return `Question ${n}: option values must be different.`;
  }
  if (q.type === 'Score') {
    const levels = q.levels.map(l => l.trim()).filter(Boolean);
    if (levels.length < 2 || levels.length > 20) return `Question ${n}: a score needs 2 to 20 levels.`;
  }
  return null;
}

function toWire(q: QuestionDraft): DecisionQuestion {
  const out: DecisionQuestion = { type: q.type };
  if (q.instructions.trim()) out.instructions = q.instructions.trim();
  if (q.type === 'Choice') {
    // A blank description falls back to the value itself — the API needs text for each label.
    out.criteria = Object.fromEntries(q.options
      .filter(o => o.label.trim())
      .map(o => [o.label.trim(), o.description.trim() || o.label.trim()]));
  } else if (q.type === 'Score') {
    out.criteria = q.levels.map(l => l.trim()).filter(Boolean);
  } else {
    const c: Record<string, string> = {};
    if (q.yesText.trim()) c.true = q.yesText.trim();
    if (q.noText.trim()) c.false = q.noText.trim();
    if (Object.keys(c).length > 0) out.criteria = c;
  }
  return out;
}

/** True when the input is a JSON array carrying image_url parts — the only image shape accepted. */
function hasImageParts(state: unknown): boolean {
  return Array.isArray(state) && state.some(p => !!p && typeof p === 'object' && (p as { type?: unknown }).type === 'image_url');
}

/** Decisions calls cost fractions of a cent; say so rather than rounding to $0.00. */
function fmtCost(v: number | null | undefined): string {
  if (v == null) return 'cost unknown';
  if (v > 0 && v < 0.0001) return 'under $0.0001';
  return fmtUsd(v);
}

function errorView(e: unknown): { message: string; code?: string; detail?: string } {
  if (e instanceof ApiError) {
    return { message: (e.code && friendlyByCode[e.code]) || e.detail || e.message, code: e.code, detail: e.detail };
  }
  return { message: e instanceof Error ? e.message : 'Something went wrong.' };
}

/**
 * Decisions mode: ask a Decisions model typed questions about some input and see the answers
 * with their probabilities. Builds the same request a caller would send to POST /api/v1/decisions.
 */
export function DecisionsPlayground({ initialModelId }: { initialModelId: string }) {
  const api = useApi();
  const ids = useId();
  const models = useQuery({ queryKey: ['models'], queryFn: api.listModels, select: ms => ms.filter(isDecisionsModel) });
  const [modelId, setModelId] = useState(initialModelId);
  const [piiMode, setPiiMode] = useState<DecisionPiiMode>('Reject');
  const [allowUnscanned, setAllowUnscanned] = useState(false);
  const [useCache, setUseCache] = useState(false);
  const [format, setFormat] = useState<'text' | 'json'>('text');
  const [input, setInput] = useState('');
  const [questions, setQuestions] = useState<QuestionDraft[]>([blankQuestion()]);
  const [copied, setCopied] = useState(false);

  useEffect(() => { if (initialModelId) setModelId(initialModelId); }, [initialModelId]);
  // Default to the first Decisions model once the list arrives.
  useEffect(() => {
    if (!modelId && models.data && models.data.length > 0) setModelId(models.data[0].modelId);
  }, [models.data, modelId]);

  const selected = (models.data ?? []).find(m => m.modelId === modelId);

  const parsed = useMemo<{ ok: true; value: unknown } | { ok: false; error: string }>(() => {
    if (format === 'text') return { ok: true, value: input };
    try {
      const v: unknown = JSON.parse(input);
      if (v === null || typeof v !== 'object') return { ok: false, error: 'JSON input must be an object or an array.' };
      return { ok: true, value: v };
    } catch (e) {
      return { ok: false, error: `Not valid JSON: ${e instanceof Error ? e.message : 'parse error'}` };
    }
  }, [format, input]);

  const images = parsed.ok && hasImageParts(parsed.value);
  const needsAck = images && piiMode !== 'None';

  const problems = questions.map((q, i) => questionProblem(q, i, questions)).filter((p): p is string => !!p);
  const blockedReason =
    !selected ? 'Pick a Decisions model'
      : input.trim() === '' ? 'Type the input to decide about'
      : !parsed.ok ? parsed.error
      : questions.length === 0 ? 'Add at least one question'
      : problems.length > 0 ? problems[0]
      : needsAck && !allowUnscanned ? 'Tick "Send images unscanned", or set Personal data to None'
      : null;

  const buildRequest = (): DecisionRequest => ({
    model: modelId,
    state: parsed.ok ? parsed.value : input,
    questions: Object.fromEntries(questions.map(q => [q.name, toWire(q)])),
    piiMode,
    allowUnscannedAttachments: images && piiMode !== 'None' ? allowUnscanned : undefined,
    cache: useCache || undefined
  });

  // The questions as they were when the run started, so editing them afterwards doesn't relabel
  // the answers on screen.
  const [ranWith, setRanWith] = useState<QuestionDraft[]>([]);
  const answersRef = useRef<HTMLElement | null>(null);
  const run = useMutation<DecisionResponse, unknown, DecisionRequest>({
    mutationFn: req => api.decide(req),
    onMutate: () => setRanWith(questions.map(q => ({ ...q, options: [...q.options], levels: [...q.levels] }))),
    // Stacked on a narrow screen the answers sit below the whole form, so bring them into view.
    onSettled: () => {
      if (window.matchMedia?.('(max-width: 900px)').matches) answersRef.current?.scrollIntoView({ block: 'start' });
    }
  });

  const copyRequest = async () => {
    const json = JSON.stringify(buildRequest(), null, 2);
    const snippet = [
      `curl -X POST "${apiBaseUrl}/decisions" \\`,
      `  -H "Authorization: Bearer $TOKEN" \\`,
      `  -H "Content-Type: application/json" \\`,
      `  -d '${json.replace(/'/g, `'\\''`)}'`
    ].join('\n');
    await navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const update = (key: number, patch: Partial<QuestionDraft>) =>
    setQuestions(qs => qs.map(q => (q.key === key ? { ...q, ...patch } : q)));

  const loadExample = () => {
    setFormat('text');
    setInput(exampleInput);
    setQuestions(exampleQuestions());
  };

  if (models.isLoading) return <div className="placeholder">Loading Decisions models...</div>;
  if (models.error) return <div className="error-banner">Could not load models: {(models.error as Error).message}</div>;

  if ((models.data ?? []).length === 0) {
    return (
      <>
        <div className="pg-sub">Ask a Decisions model to pick, judge or score. It answers with probabilities, not text.</div>
        <div className="card"><div className="placeholder">
          No Decisions models are enabled for you yet. An administrator can add one from Models, using an
          OpenRouter slug such as <span className="mono">typesafe/jev-1.13</span>.
        </div></div>
      </>
    );
  }

  const piiHint = piiOptions.find(p => p.value === piiMode)?.hint;
  const result = run.data;

  return (
    <>
      <div className="pg-sub">Ask a Decisions model to pick, judge or score. It answers with probabilities, not text.</div>
      <div className="dec-layout">
        <section className="panel" aria-labelledby={`${ids}-in`}>
          <div className="panel-head">
            <span id={`${ids}-in`}>Input and questions</span>
            <span className="panel-meta">
              <button type="button" className="link-btn" onClick={loadExample}>Load an example</button>
            </span>
          </div>
          <div className="panel-body">
            <div className="field-row">
              <div className="field">
                <label htmlFor={`${ids}-model`}>Decisions model</label>
                <select id={`${ids}-model`} value={modelId} onChange={e => setModelId(e.target.value)}>
                  {(models.data ?? []).map(m => (
                    <option key={m.modelId} value={m.modelId}>
                      {m.displayName} · {fmtUsd(m.inputCostPerMillionUsd)} per 1M input
                    </option>
                  ))}
                </select>
                {selected && (
                  <div className="hint readable">
                    <span className="mono">{selected.modelId}</span>
                    {selected.maxContextTokens ? ` · up to ${fmtNumber(selected.maxContextTokens)} input tokens` : ''}
                    {selected.supportsImageInput ? ' · reads images' : ' · text only'}
                  </div>
                )}
              </div>
              <div className="field">
                <label htmlFor={`${ids}-pii`}>Personal data</label>
                <select id={`${ids}-pii`} value={piiMode} onChange={e => setPiiMode(e.target.value as DecisionPiiMode)}
                  aria-describedby={`${ids}-pii-hint`}>
                  {piiOptions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <div className="hint readable" id={`${ids}-pii-hint`}>{piiHint}</div>
              </div>
            </div>

            <div className="field">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 5 }}>
                <label htmlFor={`${ids}-input`} style={{ marginBottom: 0 }}>Input, the text or JSON the model reads</label>
                <div className="seg-group" role="group" aria-label="Input format" style={{ marginLeft: 'auto' }}>
                  <button type="button" className={format === 'text' ? 'seg on' : 'seg'} aria-pressed={format === 'text'}
                    onClick={() => setFormat('text')}>Text</button>
                  <button type="button" className={format === 'json' ? 'seg on' : 'seg'} aria-pressed={format === 'json'}
                    onClick={() => setFormat('json')}>JSON</button>
                </div>
              </div>
              <textarea id={`${ids}-input`} rows={5} value={input} onChange={e => setInput(e.target.value)}
                className={format === 'json' ? 'mono' : undefined}
                placeholder={format === 'json'
                  ? '{"subject": "...", "body": "..."}  or an array of parts, e.g. [{"type":"text","text":"..."}, {"type":"image_url","image_url":{"url":"data:image/png;base64,..."}}]'
                  : 'e.g. an email, a chat message or a note to classify'}
                aria-invalid={!parsed.ok || undefined} />
              {!parsed.ok && input.trim() !== '' && <div className="field-error">{parsed.error}</div>}
              {images && !selected?.supportsImageInput && (
                <div className="warn-text" style={{ marginTop: 4 }}>This model reads text only — the request will be refused while the input carries images.</div>
              )}
            </div>

            {needsAck && (
              <label className="field" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '.86rem' }}>
                <input type="checkbox" style={{ width: 'auto', marginTop: 3 }} checked={allowUnscanned}
                  onChange={e => setAllowUnscanned(e.target.checked)} />
                <span>Send images unscanned. The personal-data check only covers text; images go to the supplier as they are.</span>
              </label>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0 10px' }}>
              <strong style={{ color: 'var(--kt-heading)' }}>Questions</strong>
              <span className="chip">{questions.length}</span>
              <button type="button" className="btn-secondary btn-sm" style={{ marginLeft: 'auto' }}
                disabled={questions.length >= 20}
                onClick={() => setQuestions(qs => [...qs, blankQuestion()])}>
                Add question
              </button>
            </div>

            {questions.map((q, i) => (
              <QuestionEditor key={q.key} q={q} index={i} idBase={`${ids}-q${q.key}`}
                onChange={patch => update(q.key, patch)}
                onRemove={questions.length > 1 ? () => setQuestions(qs => qs.filter(x => x.key !== q.key)) : undefined} />
            ))}

            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '.86rem', margin: '6px 0 12px', color: 'var(--kt-grey-700)' }}>
              <input type="checkbox" checked={useCache} onChange={e => setUseCache(e.target.checked)} />
              Reuse a cached answer for an identical request (free, but the same answer rather than a fresh sample)
            </label>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" className="btn-primary" disabled={!!blockedReason || run.isPending}
                onClick={() => run.mutate(buildRequest())}>
                {run.isPending ? 'Deciding...' : 'Get decisions'}
              </button>
              <button type="button" className="btn-secondary btn-sm" onClick={copyRequest}
                disabled={!selected || !parsed.ok}>
                {copied ? 'Copied' : 'Copy as API request'}
              </button>
              {blockedReason && <span className="warn-text" role="status">{blockedReason}</span>}
            </div>
          </div>
        </section>

        <section ref={answersRef} className="panel dec-answers" aria-labelledby={`${ids}-out`} aria-busy={run.isPending}>
          <div className="panel-head">
            <span id={`${ids}-out`}>Answers</span>
            {result && (
              <span className="panel-meta">
                <span className="mono">{result.servedModel ?? result.modelId}</span>
                {' · '}{fmtNumber(result.usage.latencyMs)} ms
                {' · '}{fmtCost(result.usage.estimatedCostUsd)}
              </span>
            )}
          </div>
          <div className="panel-body" aria-live="polite">
            {run.isPending && <div className="placeholder">Asking the model...</div>}
            {run.isError && <DecisionError error={run.error} />}
            {!run.isPending && !run.isError && !result && (
              <div className="placeholder">Answers appear here, each with how likely the model thinks it is.</div>
            )}
            {!run.isPending && result && (
              <>
                {result.modelSubstitution && (
                  <div className="info-banner">
                    {result.modelSubstitution.requestedModelId} is switched off, so {result.modelId} answered instead
                    {result.modelSubstitution.reason ? ` (${result.modelSubstitution.reason})` : ''}.
                  </div>
                )}
                {ranWith.map(q => {
                  const a = result.answers[q.name];
                  return a ? <AnswerView key={q.key} q={q} a={a} /> : (
                    <div key={q.key} className="ans text-muted">No answer came back for <strong>{q.name}</strong>.</div>
                  );
                })}
                <div className="ans-foot" style={{ marginTop: 10 }}>
                  {fmtNumber(result.usage.inputTokens)} input tokens · billed on input tokens only.
                  {result.servedFromGatewayCache ? ' Served from the gateway cache — no supplier call, no cost.' : ' Answers vary a little between runs, so results aren’t cached unless you ask.'}
                  {result.piiRedactionApplied ? ' Personal data was redacted before sending.' : ''}
                  {result.provider ? ` Routed to ${result.provider}.` : ''}
                </div>
                <div className="ans-foot">
                  Correlation id <span className="mono">{result.correlationId}</span> · <Link to="/usage">My usage</Link>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </>
  );
}

function DecisionError({ error }: { error: unknown }) {
  const { message, code, detail } = errorView(error);
  return (
    <div className="error-banner" role="alert">
      <div><strong>{message}</strong></div>
      {code && (
        <div style={{ marginTop: 4, fontSize: '.84rem' }}>
          <span className="mono">{code}</span>{detail && detail !== message ? ` — ${detail}` : ''}
        </div>
      )}
    </div>
  );
}

function QuestionEditor({ q, index, idBase, onChange, onRemove }: {
  q: QuestionDraft;
  index: number;
  idBase: string;
  onChange: (patch: Partial<QuestionDraft>) => void;
  onRemove?: () => void;
}) {
  const label = q.name || `question ${index + 1}`;
  const setOption = (i: number, patch: Partial<ChoiceOption>) =>
    onChange({ options: q.options.map((o, idx) => (idx === i ? { ...o, ...patch } : o)) });
  const setLevel = (i: number, value: string) => onChange({ levels: q.levels.map((l, idx) => (idx === i ? value : l)) });
  const moveLevel = (i: number, by: -1 | 1) => {
    const next = [...q.levels];
    const j = i + by;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange({ levels: next });
  };

  return (
    <fieldset className="q-card">
      <legend className="sr-only">Question {index + 1}</legend>
      <div className="q-card-head">
        <div className="field" style={{ width: 140 }}>
          <label htmlFor={`${idBase}-type`}>Type</label>
          <select id={`${idBase}-type`} value={q.type} onChange={e => onChange({ type: e.target.value as DecisionQuestionType })}>
            {(Object.keys(typeLabels) as DecisionQuestionType[]).map(t => <option key={t} value={t}>{typeLabels[t]}</option>)}
          </select>
        </div>
        <div className="field" style={{ flex: 1, minWidth: 160 }}>
          <label htmlFor={`${idBase}-name`}>Name</label>
          <input id={`${idBase}-name`} type="text" className="mono" placeholder="e.g. category" value={q.name}
            onChange={e => onChange({ name: e.target.value })}
            aria-invalid={(q.name !== '' && !namePattern.test(q.name)) || undefined} />
        </div>
        {onRemove && (
          <button type="button" className="btn-ghost btn-sm" onClick={onRemove} aria-label={`Remove ${label}`}>Remove</button>
        )}
      </div>
      {q.name !== '' && !namePattern.test(q.name) && (
        <div className="field-error" style={{ marginBottom: 6 }}>Letters, digits, _ or - only, up to 64 characters.</div>
      )}
      <div className="field">
        <label htmlFor={`${idBase}-instr`}>Instructions</label>
        <input id={`${idBase}-instr`} type="text" placeholder="What should the model decide, in plain words?"
          value={q.instructions} onChange={e => onChange({ instructions: e.target.value })} />
      </div>

      {q.type === 'Choice' && (
        <div>
          <div className="text-muted" style={{ fontSize: '.8rem', marginBottom: 4 }}>Options: the value returned, and what it means</div>
          {q.options.map((o, i) => (
            <div className="q-opt" key={i}>
              <input type="text" className="mono" placeholder="value" value={o.label}
                aria-label={`Option ${i + 1} value for ${label}`} onChange={e => setOption(i, { label: e.target.value })} />
              <input type="text" placeholder="description (optional)" value={o.description}
                aria-label={`Option ${i + 1} description for ${label}`} onChange={e => setOption(i, { description: e.target.value })} />
              <button type="button" className="btn-ghost btn-sm" disabled={q.options.length <= 2}
                aria-label={`Remove option ${i + 1} from ${label}`}
                onClick={() => onChange({ options: q.options.filter((_, idx) => idx !== i) })}>Remove</button>
            </div>
          ))}
          <button type="button" className="link-btn" style={{ fontSize: '.86rem' }} disabled={q.options.length >= 50}
            onClick={() => onChange({ options: [...q.options, { label: '', description: '' }] })}>
            Add option
          </button>
        </div>
      )}

      {q.type === 'Noul' && (
        <div className="field-row">
          <div className="field">
            <label htmlFor={`${idBase}-yes`}>Yes means (optional)</label>
            <input id={`${idBase}-yes`} type="text" value={q.yesText} onChange={e => onChange({ yesText: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor={`${idBase}-no`}>No means (optional)</label>
            <input id={`${idBase}-no`} type="text" value={q.noText} onChange={e => onChange({ noText: e.target.value })} />
          </div>
        </div>
      )}

      {q.type === 'Score' && (
        <div>
          <div className="text-muted" style={{ fontSize: '.8rem', marginBottom: 4 }}>Levels, lowest first</div>
          {q.levels.map((l, i) => (
            <div className="q-opt level" key={i}>
              <span className="q-idx">{i}</span>
              <input type="text" placeholder={i === 0 ? 'lowest, e.g. Routine' : 'next level up'} value={l}
                aria-label={`Level ${i} for ${label}`} onChange={e => setLevel(i, e.target.value)} />
              <span style={{ display: 'inline-flex', gap: 2 }}>
                <button type="button" className="btn-ghost btn-sm" disabled={i === 0} onClick={() => moveLevel(i, -1)}
                  aria-label={`Move level ${i} of ${label} up`}>Up</button>
                <button type="button" className="btn-ghost btn-sm" disabled={i === q.levels.length - 1} onClick={() => moveLevel(i, 1)}
                  aria-label={`Move level ${i} of ${label} down`}>Down</button>
                <button type="button" className="btn-ghost btn-sm" disabled={q.levels.length <= 2}
                  onClick={() => onChange({ levels: q.levels.filter((_, idx) => idx !== i) })}
                  aria-label={`Remove level ${i} from ${label}`}>Remove</button>
              </span>
            </div>
          ))}
          <button type="button" className="link-btn" style={{ fontSize: '.86rem' }} disabled={q.levels.length >= 20}
            onClick={() => onChange({ levels: [...q.levels, ''] })}>
            Add level
          </button>
        </div>
      )}
    </fieldset>
  );
}

function ProbRow({ label, value }: { label: string; value: number }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div className="prow">
      <span className="k" title={label}>{label}</span>
      <span className="pbar" role="img" aria-label={`${label}: ${value.toFixed(2)}`}><span style={{ width: `${pct}%` }} /></span>
      <span className="v">{value.toFixed(2)}</span>
    </div>
  );
}

function AnswerView({ q, a }: { q: QuestionDraft; a: DecisionAnswer }) {
  let value = '';
  let rows: { label: string; value: number }[] = [];
  let foot: string | null = null;

  if (q.type === 'Choice') {
    value = a.choice ?? '—';
    rows = Object.entries(a.probabilities ?? {})
      .map(([label, p]) => ({ label, value: p }))
      .sort((x, y) => y.value - x.value);
    if (a.confidence != null) foot = `Confidence ${a.confidence.toFixed(2)}`;
  } else if (q.type === 'Noul') {
    const p = a.noul ?? 0;
    value = a.noul == null ? '—' : p >= 0.5 ? 'Yes' : 'No';
    rows = [{ label: 'Probability of yes', value: p }];
  } else {
    const levels = q.levels.map(l => l.trim()).filter(Boolean);
    const legend = a.legend ?? {};
    const count = Math.max(levels.length, Object.keys(a.probabilities ?? {}).length);
    rows = Array.from({ length: count }, (_, i) => ({
      label: legend[String(i)] ?? levels[i] ?? `Level ${i}`,
      value: a.probabilities?.[String(i)] ?? 0
    }));
    if (a.score != null) {
      const nearest = Math.round(a.score);
      value = legend[String(nearest)] ?? levels[nearest] ?? a.score.toFixed(2);
      foot = `Score ${a.score.toFixed(2)} on a 0–${count - 1} scale`
        + (a.confidence != null ? ` · confidence ${a.confidence.toFixed(2)}` : '');
    }
  }

  return (
    <div className="ans">
      <div className="ans-head">
        <span className="q-tag">{typeLabels[q.type]}</span>
        <strong className="mono">{q.name}</strong>
        <span className="ans-value">{value}</span>
      </div>
      {rows.map((r, i) => <ProbRow key={`${i}-${r.label}`} label={r.label} value={r.value} />)}
      {foot && <div className="ans-foot">{foot}</div>}
    </div>
  );
}
